import { useEffect } from 'react'
import { Link, Outlet, createRootRouteWithContext, type ErrorComponentProps } from '@tanstack/react-router'
import type { QueryClient } from '@tanstack/react-query'
import { Toaster } from 'sonner'
import { FileQuestion } from 'lucide-react'
import { EmptyState, ErrorState } from '@/components/empty-state'
import { Button } from '@/components/ui/button'
import { useBrandingBootstrap } from '@/lib/branding'
import { reportClientError } from '@/lib/monitoring'
import { resolveDark, usePreferencesBootstrap, usePreferencesStore } from '@/lib/preferences'
import { ROUTES } from '@/lib/routes'

/** Contexto disponible en `beforeLoad`/`loader` de todas las rutas. */
export interface RouterContext {
  queryClient: QueryClient
}

export const Route = createRootRouteWithContext<RouterContext>()({
  component: RootComponent,
  notFoundComponent: NotFound,
  errorComponent: RouteError,
})

/** Error de una pantalla: se muestra con opción de reintentar y se reporta al monitoreo. */
function RouteError({ error, reset }: ErrorComponentProps) {
  useEffect(() => {
    void reportClientError(error)
  }, [error])
  return (
    <div className="mx-auto max-w-lg p-6">
      <ErrorState error={error} onRetry={reset} title="Algo salió mal en esta pantalla" />
    </div>
  )
}

function RootComponent() {
  useBrandingBootstrap()
  usePreferencesBootstrap()
  const theme = usePreferencesStore((s) => s.theme)
  return (
    <>
      <Outlet />
      <Toaster
        position="top-center"
        richColors
        closeButton
        theme={resolveDark(theme) ? 'dark' : 'light'}
        toastOptions={{ style: { fontFamily: 'var(--font-sans)' } }}
      />
    </>
  )
}

function NotFound() {
  return (
    <div className="flex min-h-dvh items-center justify-center p-6">
      <EmptyState
        icon={FileQuestion}
        title="Página no encontrada"
        description="La dirección no existe o fue movida."
        action={
          <Button asChild>
            <Link to={ROUTES.dashboard}>Ir al panel</Link>
          </Button>
        }
      />
    </div>
  )
}
